// ─────────────────────────────────────────────────────────────────────────────
// Crew — group home: members + active goal + time poll (Agent B).
//
// Steps of the funnel surfaced here: see your crew → create a goal → commit it →
// find a time. All funnel events for these steps are fired SERVER-SIDE; the client
// fires nothing here. There is no GET-goals endpoint, so the active goal lives in
// client funnel state (mirrored to sessionStorage) once created this session.
//
// Monetisation NEVER gates goal creation conceptually, but the free tier caps the
// number of ACTIVE goals (server returns 402). We surface that limit honestly.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, type FormEvent } from 'react';
import type { Goal, Group, Member, TimePoll as Poll } from '../lib/types';
import { api, ApiError } from '../lib/api';
import { Button } from '../components/Button';
import { Card, CardTitle } from '../components/Card';
import { SelectField, TextField } from '../components/Field';
import { ErrorBanner } from '../components/Feedback';
import { navigate } from '../lib/router';
import { errorMessage } from '../lib/format';
import { TimePoll } from './TimePoll';

interface GroupHomeProps {
  group: Group;
  members: Member[];
  me: Member;
  inviteCode: string;
  goal: Goal | null;
  poll: Poll | null;
  onGoal: (goal: Goal) => void;
  onPoll: (poll: Poll) => void;
}

const GOAL_TYPES: { value: Goal['type']; label: string }[] = [
  { value: 'trip', label: 'Trip' },
  { value: 'fitness', label: 'Fitness' },
  { value: 'recurring_meetup', label: 'Recurring meetup' },
  { value: 'skill', label: 'Skill' },
  { value: 'project', label: 'Project' },
  { value: 'custom', label: 'Custom' },
];

function MemberAvatar({ member, you }: { member: Member; you: boolean }) {
  const initials = member.displayName.slice(0, 2).toUpperCase();
  return (
    <div className="flex items-center gap-2">
      <span
        aria-hidden
        className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-white"
        style={{ backgroundColor: member.avatarColor }}
      >
        {initials}
      </span>
      <span className="text-sm text-ink-800">
        {member.displayName}
        {member.role === 'owner' && <span className="ml-1 text-xs text-ink-700/60">(owner)</span>}
        {you && <span className="ml-1 text-xs text-coral-600">· you</span>}
      </span>
    </div>
  );
}

export function GroupHome({ group, members, me, inviteCode, goal, poll, onGoal, onPoll }: GroupHomeProps) {
  const isOwner = me.role === 'owner';

  // create-goal form
  const [title, setTitle] = useState('');
  const [type, setType] = useState<Goal['type']>('trip');
  const [creating, setCreating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // The goal's serialized shape has no "committed" boolean; commit fires a server
  // event and unlocks the next step. We track that locally for this session.
  const [committed, setCommitted] = useState(false);

  async function createGoal(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const res = await api.createGoal(group.id, { title: title.trim(), type });
      onGoal(res.goal);
      setTitle('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        setError('You’ve reached the active-goal limit on the free tier. Complete or pause a goal to add another.');
      } else {
        setError(errorMessage(err, 'Could not create the goal. Please try again.'));
      }
    } finally {
      setCreating(false);
    }
  }

  async function commit() {
    if (!goal) return;
    setCommitting(true);
    setError(null);
    try {
      await api.commitGoal(goal.id);
      // commitGoal returns { ok }; reflect the committed state locally so the UI
      // advances to the time-poll step without a list endpoint to re-read from.
      onGoal({ ...goal, status: 'active', lastActivityAt: new Date().toISOString() });
      // Mark commitment via a sentinel on the goal copy so the next render shows
      // the poll step. We track "committed" with a local flag below.
      setCommitted(true);
    } catch (err) {
      setError(errorMessage(err, 'Could not commit the goal. Please try again.'));
    } finally {
      setCommitting(false);
    }
  }

  async function copyInvite() {
    try {
      await navigator.clipboard.writeText(inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — code is visible to copy by hand */
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink-900">{group.name}</h1>
        <p className="mt-1 text-sm text-ink-700/70">
          {members.length} {members.length === 1 ? 'member' : 'members'}
        </p>
      </div>

      {error && <ErrorBanner message={error} />}

      {/* Members + invite */}
      <Card>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>Your crew</CardTitle>
          <span className="font-mono text-sm font-semibold tracking-widest text-coral-600">{inviteCode}</span>
        </div>
        <ul className="mt-4 flex flex-col gap-3">
          {members.map((m) => (
            <li key={m.id}>
              <MemberAvatar member={m} you={m.id === me.id} />
            </li>
          ))}
        </ul>
        <div className="mt-4">
          <Button variant="secondary" onClick={copyInvite}>
            {copied ? 'Invite code copied!' : 'Copy invite code'}
          </Button>
        </div>
      </Card>

      {/* Goal */}
      {goal ? (
        <Card>
          <CardTitle>Active goal</CardTitle>
          <p className="mt-2 text-lg font-medium text-ink-900">{goal.title}</p>
          <p className="text-sm text-ink-700/70 capitalize">{goal.type.replace('_', ' ')}</p>
          {!committed ? (
            <div className="mt-4">
              <p className="text-sm text-ink-700">
                Commit to lock this goal in for the crew, then find a time to make it happen.
              </p>
              <div className="mt-3">
                <Button onClick={commit} busy={committing}>
                  Commit goal
                </Button>
              </div>
            </div>
          ) : (
            <p className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-coral-600">
              Committed — let’s find a time below.
            </p>
          )}
        </Card>
      ) : (
        <Card>
          <CardTitle>Set a goal</CardTitle>
          <p className="mt-1 text-sm text-ink-700/70">What do you want to do together?</p>
          <form onSubmit={createGoal} className="mt-4 flex flex-col gap-4">
            <TextField
              label="Goal"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Weekend trip to the Peak District"
            />
            <SelectField label="Type" value={type} onChange={(e) => setType(e.target.value as Goal['type'])}>
              {GOAL_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </SelectField>
            <Button type="submit" busy={creating} disabled={!title.trim()}>
              Create goal
            </Button>
          </form>
        </Card>
      )}

      {/* Time poll — only once the goal is committed */}
      {goal && committed && (
        <TimePoll
          groupId={group.id}
          goalId={goal.id}
          poll={poll}
          members={members}
          me={me}
          isOwner={isOwner}
          onPoll={onPoll}
        />
      )}

      {/* Hand-off to activities once a time is locked */}
      {poll?.status === 'locked' && (
        <Button onClick={() => navigate('activities')}>Browse activities</Button>
      )}
    </div>
  );
}
